/**
 * Library browsing model — pure helpers for the /library toolbar.
 *
 * Kept free of React and DOM APIs (except the narrowly-guarded localStorage
 * accessors at the bottom) so the search / sort / filter behaviour can be
 * tested without rendering.
 */

import type { BookSummary } from "@/lib/lumi-api";

// ── Kind (book vs saved article) ──────────────────────────────────────────

export type BookKind = "book" | "article";

/** Normalize the API's nullable `kind` column. Anything unknown reads as a book. */
export function bookKind(book: Pick<BookSummary, "kind">): BookKind {
  return book.kind === "article" ? "article" : "book";
}

export type KindFilter = "all" | BookKind;

export const KIND_FILTERS: KindFilter[] = ["all", "book", "article"];

export function isKindFilter(raw: unknown): raw is KindFilter {
  return raw === "all" || raw === "book" || raw === "article";
}

// ── Sort ──────────────────────────────────────────────────────────────────

/**
 * `updated` is the closest signal the list endpoint carries to "recently
 * touched" — the books row is bumped on edits and on translation progress.
 *
 * `read` uses `last_read_at` (the API's `reading_progress.updated_at` for the
 * signed-in user). That is "last progress saved", not "last opened" — opening a
 * book and leaving without reading does not bump it, which is exactly what a
 * "Recently read" shelf should show.
 */
export type SortKey = "added" | "updated" | "read" | "title";

export const SORT_OPTIONS: Array<{ value: SortKey; label: string }> = [
  { value: "added", label: "Recently added" },
  { value: "updated", label: "Recently updated" },
  { value: "read", label: "Recently read" },
  { value: "title", label: "Title (A–Z)" },
];

export function isSortKey(raw: unknown): raw is SortKey {
  return (
    raw === "added" || raw === "updated" || raw === "read" || raw === "title"
  );
}

function time(value: string | null | undefined): number {
  if (!value) return 0;
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? 0 : ms;
}

// ── Search ────────────────────────────────────────────────────────────────

/**
 * Fold case and strip diacritics so "fabulas" finds "Fábulas". Falls back to a
 * plain lowercase compare where `normalize` is unavailable.
 */
export function normalizeSearchText(value: string): string {
  const lower = value.toLowerCase().trim();
  try {
    return lower.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  } catch {
    return lower;
  }
}

/** Every token in the query must appear somewhere in the book's haystack. */
export function matchesQuery(
  book: Pick<BookSummary, "title" | "target_language" | "source_language">,
  query: string,
): boolean {
  const tokens = normalizeSearchText(query).split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return true;
  const haystack = normalizeSearchText(
    [book.title, book.target_language, book.source_language]
      .filter(Boolean)
      .join(" "),
  );
  return tokens.every((token) => haystack.includes(token));
}

// ── Combined view ─────────────────────────────────────────────────────────

export interface LibraryViewOptions {
  query: string;
  kind: KindFilter;
  sort: SortKey;
}

export function libraryView(
  books: BookSummary[],
  { query, kind, sort }: LibraryViewOptions,
): BookSummary[] {
  const filtered = books.filter(
    (book) =>
      (kind === "all" || bookKind(book) === kind) && matchesQuery(book, query),
  );

  const sorted = [...filtered];
  if (sort === "title") {
    sorted.sort((a, b) =>
      a.title.localeCompare(b.title, undefined, { sensitivity: "base" }),
    );
  } else if (sort === "updated") {
    sorted.sort((a, b) => time(b.updated_at) - time(a.updated_at));
  } else if (sort === "read") {
    // Most recently read first. Never-read books have no `last_read_at`, so
    // they score 0 and fall to the bottom of the list rather than the top.
    // Ties — including the whole never-read tail — break on `created_at`
    // descending, matching the "Recently added" order the user already knows.
    sorted.sort(
      (a, b) =>
        time(b.last_read_at) - time(a.last_read_at) ||
        time(b.created_at) - time(a.created_at),
    );
  } else {
    sorted.sort((a, b) => time(b.created_at) - time(a.created_at));
  }
  return sorted;
}

export function countByKind(books: BookSummary[]): Record<BookKind, number> {
  return books.reduce(
    (acc, book) => {
      acc[bookKind(book)] += 1;
      return acc;
    },
    { book: 0, article: 0 },
  );
}

// ── Cover style setting (Flat / 3D) ───────────────────────────────────────

export type CoverStyle = "flat" | "solid";

export const COVER_STYLE_STORAGE_KEY = "lumi-library-cover-style";
export const DEFAULT_COVER_STYLE: CoverStyle = "flat";

export function normalizeCoverStyle(raw: unknown): CoverStyle | null {
  if (raw === "flat" || raw === "solid") return raw;
  // Legacy/alias spellings from earlier builds and hand-set values.
  if (typeof raw === "string") {
    const lower = raw.trim().toLowerCase();
    if (lower === "3d" || lower === "book" || lower === "solid") return "solid";
    if (lower === "2d" || lower === "flat") return "flat";
  }
  return null;
}

export function readStoredCoverStyle(): CoverStyle {
  if (typeof window === "undefined") return DEFAULT_COVER_STYLE;
  try {
    return (
      normalizeCoverStyle(localStorage.getItem(COVER_STYLE_STORAGE_KEY)) ??
      DEFAULT_COVER_STYLE
    );
  } catch {
    return DEFAULT_COVER_STYLE;
  }
}

export function writeStoredCoverStyle(style: CoverStyle): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(COVER_STYLE_STORAGE_KEY, style);
  } catch {
    // private mode / quota — the setting just won't persist
  }
}
